import React, { useState, useRef, useEffect, useMemo } from 'react';
import { testId } from '../../../../utils/testIds';
import { UNSELECTED_OPTION_VALUE } from '../../constants';

const norm = s => String(s ?? '').replace(/\s+/g, ' ').trim().toLowerCase();

function SearchableSelectField({
  config, value, onChange, onBlur, disabled, required, options = [], error,
}) {
  const [searchText, setSearchText] = useState('');
  const [isOpen,     setIsOpen]     = useState(false);
  const wrapRef = useRef(null);
  const wasOpen = useRef(false);
  const cleanOptions = useMemo(() => {
    const seen = new Set();
    return options.filter(o => {
      const k = String(o?.value ?? '');
      if (!k || seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [options]);

  const selectedLabel = useMemo(
    () => cleanOptions.find(o => norm(o.value) === norm(value))?.label ?? '',
    [cleanOptions, value],
  );

  useEffect(() => {
    if(!isOpen) setSearchText(selectedLabel)
  }, [value, isOpen, selectedLabel])

  const filtered = useMemo(() => {
    const selectable = cleanOptions.filter(o => String(o.value) !== UNSELECTED_OPTION_VALUE);
    const q = norm(searchText);
    if (!q) return selectable;
    return selectable.filter(o => norm(o.label).includes(q));
  }, [searchText, cleanOptions]);

  useEffect(() => {
    if (!isOpen) return;
    function handleOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setIsOpen(false);
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [isOpen]);

  function handleFocus() {
    if (isOpen) return;
    setSearchText('');
    setIsOpen(true);
    config?.fieldSettings?.onFocus?.();
  }

  function handleChange(e) {
    setSearchText(e.target.value);
    if (!isOpen) setIsOpen(true);
  }

  function handleSelect(opt) {
    onChange(String(opt.value));
    setSearchText(opt.label);
    setIsOpen(false);
    onBlur?.();
  }

  const placeholder = config?.fieldSettings?.placeholder ?? 'Search...';

  return (
    <div ref={wrapRef} className={`field-engine-searchable-select${disabled ? ' disabled' : ''}`}>
      <input
        data-test={testId('field', config?.fieldKey, 'input')}
        type="text"
        className={`field-engine-input${error ? ' field-engine-input--error' : ''}${disabled ? ' disabled' : ''}`}
        value={searchText}
        onChange={handleChange}
        onFocus={handleFocus}
        disabled={disabled}
        required={required}
        placeholder={placeholder}
        autoComplete="off"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-required={required}
        aria-invalid={!!error}
      />
      {isOpen && (
        <ul className="field-engine-searchable-dropdown" role="listbox">
          {filtered.length > 0 ? (
            filtered.map(opt => (
              <li
                key={String(opt.value)}
                role="option"
                aria-selected={norm(opt.value) === norm(value)}
                className={`field-engine-searchable-option${norm(opt.value) === norm(value) ? ' field-engine-searchable-option--selected' : ''}`}
                onMouseDown={e => { e.preventDefault(); handleSelect(opt); }}
              >
                {opt.label}
              </li>
            ))
          ) : (
            <li className="field-engine-searchable-option field-engine-searchable-option--empty">
              No options found
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

export default SearchableSelectField;
